import InfoIcon from '@mui/icons-material/Info';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { createSelector } from '@reduxjs/toolkit';
import InputCheckbox from 'common/components/Elements/Form/InputCheckbox/InputCheckbox';
import InputRadioGroup from 'common/components/Elements/Form/InputRadioGroup/InputRadioGroup';
import InputSelect from 'common/components/Elements/Form/InputSelect/InputSelect';
import InputText from 'common/components/Elements/Form/InputText/InputText';
import Icon from 'common/components/Elements/Icon/Icon';
import ModalActions from 'common/components/Elements/Modal/ModalActions/ModalActions';
import SimpleModal from 'common/components/Elements/Modal/SimpleModal/SimpleModal';
import { generateRandomNumber } from 'common/utilities/core/number';
import * as I from 'core/api/coreApiClient';
import type { ApplicationState } from 'core/redux/reducers/rootReducer';
import type { ValueLabelOption } from 'core/utilities/core.types';
import { formatDatetime } from 'core/utilities/dateFormat/dateFormat';
import I18n from 'i18n/en-US';
import React from 'react';
import { useSelector } from 'react-redux';
import type { EntitySequence as Sequence, SequenceAlign, SequenceType } from 'sequenceAnalyser/api/sequenceAnalyserApiClient';
import type { HandleInsertSequences } from 'sequenceAnalyser/components/SequenceAnalyser/SequenceAnalyser';
import type { SequenceSelection } from 'sequenceAnalyser/utilities/selection/selection';
import { removeAllGaps } from 'sequenceAnalyser/utilities/selectionActions/removeGaps';
import {
  createSequenceFromManualInsert, createSequencesFromSequenceSelection, generateSequenceQualifiedId,
  SequenceQualifiedIdTypes,
} from 'sequenceAnalyser/utilities/selectionActions/selectionActions';
import { isValidResidues } from 'sequenceAnalyser/utilities/sequenceAnalyser/sequenceAnalyser';

export interface ManualSequence {
  name: string;
  residues: string;
  rawSequence?: Sequence;
  sequence?: Sequence;
  alignedSequence?: SequenceAlign;
}

function residuesFieldErrorMsg(
  residues: string, sequenceType: SequenceType,
): I.Message[] | undefined {
  if (residues.length === 0) {
    return [{ severity: I.SeverityConstants.ERROR, text: I18n.SequenceAnalyser.InsertActionModal.required }];
  } if (!isValidResidues(residues, sequenceType)) {
    return [{ severity: I.SeverityConstants.ERROR, text: I18n.SequenceAnalyser.InsertActionModal.invalidResidues }];
  }
  return undefined;
}

function getNewManualSequence(): ManualSequence {
  return { name: '', residues: '' };
}

export interface ComponentProps {
  sequences: Sequence[];
  alignedSequences: SequenceAlign[];
  open: boolean;
  onOpen: (open: boolean) => void;
  onInsertSequences: HandleInsertSequences;
  selection: SequenceSelection;
  sessionSequenceType: SequenceType;
  mode: 'manual' | 'sessionStore';
  manualSequence?: ManualSequence;
}

const storedSequenceSelectionSelector = createSelector(
  [(state: ApplicationState) => state.sequenceAnalyser],
  (sequenceAnalyser) => [...sequenceAnalyser.storedSequenceSelection].reverse(),
);

export function InsertActionModal({
  open, onOpen, sequences, alignedSequences, onInsertSequences, selection, sessionSequenceType,
  mode, manualSequence,
}: ComponentProps) {
  const configuration = useSelector((state: ApplicationState) => (
    state.configuration.configuration
  ));

  // Stored sequences
  const storedSequenceSelection = useSelector(storedSequenceSelectionSelector);
  const [storedSequenceSelectionId, setStoredSequenceSelectionId] = React.useState<string | undefined>(
    storedSequenceSelection.length > 0 ? '0' : undefined,
  );
  const [removeGaps, setRemoveGaps] = React.useState(false);

  // New (manual) sequence data
  const [newManualSequence, setNewManualSequence] = React.useState(manualSequence || getNewManualSequence());

  // Position
  const lastSelectedId = selection.ids[selection.ids.length - 1];
  const lastId = alignedSequences.length;

  const insertPositions: ValueLabelOption[] = [{ value: '0', label: 'Beginning' }];

  if (lastSelectedId) {
    const id = alignedSequences.findIndex((s) => s.id === lastSelectedId) + 1;

    if (lastId !== id) {
      insertPositions.push({
        value: id.toString(),
        label: 'After last selected sequence',
      });
    }
  }

  if (alignedSequences.length > 0) {
    insertPositions.push({ value: (alignedSequences.length).toString(), label: 'End' });
  }

  const [insertPosition, setInsertPosition] = React.useState(insertPositions[0].value);

  // Submit errors
  const [showErrors, setShowErrors] = React.useState(false);

  return (
    <SimpleModal
      title={I18n.SequenceAnalyser.InsertActionModal[mode]}
      open={open}
      onClose={() => {
        onOpen(false);
      }}
      content={(
        <Box mt={1}>
          <InputRadioGroup
            label="Position to insert sequences"
            options={insertPositions}
            onChange={(value) => setInsertPosition(value)}
            value={insertPosition}
            horizontal
            modalLabel
          />
          {mode === 'sessionStore' && (
            <>
              <InputSelect
                label="Stored sequences"
                value={storedSequenceSelectionId}
                options={storedSequenceSelection.map((s, i) => ({
                  value: i.toString(),
                  label: [
                    formatDatetime(s.createdDate, configuration),
                    ' - ',
                    `${s.sequences.length} sequences`,
                    s.positionStart !== undefined && s.positionEnd !== undefined
                      ? ` - ${s.positionEnd - s.positionStart + 1} residues`
                      : ` - ${s.alignedSequences[0].residues.length} residues`,
                  ].join(''),
                }))}
                onChange={(selectedId) => {
                  setStoredSequenceSelectionId(selectedId);
                }}
                neverEmpty
              />
              <InputCheckbox
                label={I18n.SequenceAnalyser.InsertActionModal.removeGaps}
                checked={removeGaps}
                onChange={() => {
                  setRemoveGaps((prevState) => !prevState);
                }}
              />
            </>
          )}

          {mode === 'manual' && (
            <>
              <InputText
                label={I18n.SequenceAnalyser.InsertActionModal.name}
                value={newManualSequence.name}
                onChange={((value) => setNewManualSequence({ ...newManualSequence, name: value }))}
                fullWidth
                modalLabel
              />
              <InputText
                label={I18n.SequenceAnalyser.InsertActionModal.residues}
                value={newManualSequence.residues}
                onChange={((value) => {
                  const newResidues = value.replace(/[0-9]/g, '').replace(/\s/g, '').toUpperCase();
                  setNewManualSequence({ ...newManualSequence, residues: newResidues });
                })}
                fullWidth
                required
                errorMsg={showErrors
                  ? residuesFieldErrorMsg(newManualSequence.residues, sessionSequenceType)
                  : undefined}
                modalLabel
              />
              {newManualSequence.sequence && (
                <Typography variant="body2">
                  <Icon element={InfoIcon} mr />
                  {I18n.SequenceAnalyser.InsertActionModal.featuresRetained}
                </Typography>
              )}
            </>
          )}
          <ModalActions
            applyText="Insert"
            disableApply={storedSequenceSelectionId === undefined && newManualSequence.residues.length === 0}
            onApply={() => {
              // Insert can be be:
              // - manual: we create a new sequence and aligned sequence with the given amino acids
              // - sessionStore: copy from a user's selection
              //           prefix ids if a unique id so there is no chance to collide with existing ids

              if (mode === 'sessionStore') {
                const sequenceSelection = storedSequenceSelection[Number(storedSequenceSelectionId)];

                const {
                  newRawSequences, newSequences, newAlignedSequences,
                } = createSequencesFromSequenceSelection(sequenceSelection, removeGaps);

                // Callback to insert new sequence(s) at the right position
                onInsertSequences(newRawSequences, newSequences, newAlignedSequences, Number(insertPosition));
              }

              if (mode === 'manual') {
                if (
                  newManualSequence.residues.length === 0
                  || !isValidResidues(newManualSequence.residues, sessionSequenceType)
                ) {
                  setShowErrors(true);
                  return;
                }

                // Duplicate given sequence
                if (newManualSequence.sequence && newManualSequence.rawSequence && newManualSequence.alignedSequence) {
                  // No change within the sequence: keep the features (gaps can be removed)
                  if (
                    removeAllGaps(newManualSequence.alignedSequence.residues)
                    === removeAllGaps(newManualSequence.residues)
                  ) {
                    const newAlignedSequence: SequenceAlign = {
                      id: generateRandomNumber(),
                      residues: newManualSequence.residues,
                    };

                    const newSequence: Sequence = {
                      ...newManualSequence.sequence,
                      id: newAlignedSequence.id,
                      entityLabel: undefined,
                      name: newManualSequence.name,
                      features: newManualSequence.rawSequence.features, // positions will be adjusted in the handler
                    };

                    const newRawSequence: Sequence = {
                      ...newManualSequence.rawSequence,
                      id: newAlignedSequence.id,
                      entityLabel: undefined,
                      name: newManualSequence.name,
                    };

                    // Callback to insert new sequence(s) at the right position
                    onInsertSequences([newRawSequence], [newSequence], [newAlignedSequence], Number(insertPosition));

                  // Some residues have been removed: remove the existing features as they are not matching anymore
                  } else {
                    const newAlignedSequence: SequenceAlign = {
                      id: generateRandomNumber(),
                      residues: newManualSequence.residues,
                    };

                    const newSequence: Sequence = {
                      ...newManualSequence.sequence,
                      id: newAlignedSequence.id,
                      entityLabel: undefined,
                      name: newManualSequence.name,
                      residues: removeAllGaps(newAlignedSequence.residues),
                      features: [],
                    };

                    const newRawSequence: Sequence = {
                      ...newManualSequence.rawSequence,
                      id: newAlignedSequence.id,
                      entityLabel: undefined,
                      name: newManualSequence.name,
                      residues: removeAllGaps(newAlignedSequence.residues),
                      features: [],
                    };

                    // Callback to insert new sequence(s) at the right position
                    onInsertSequences([newRawSequence], [newSequence], [newAlignedSequence], Number(insertPosition));
                  }

                // Insert sequence entered manually
                } else {
                  const newQualifiedId = generateSequenceQualifiedId(
                    SequenceQualifiedIdTypes.ManuallyInserted, sequences,
                  );

                  const { newSequence, newAlignedSequence } = createSequenceFromManualInsert(
                    newQualifiedId, newManualSequence.name, newManualSequence.residues, sessionSequenceType,
                  );

                  const newRawSequence: Sequence = {
                    ...newSequence,
                    // Raw sequence shouldn't contain any gap
                    residues: removeAllGaps(newSequence.residues),
                  };

                  // Callback to insert new sequence(s) at the right position
                  onInsertSequences([newRawSequence], [newSequence], [newAlignedSequence], Number(insertPosition));
                }
              }

              // Close modal
              onOpen(false);

              // Reset data
              setNewManualSequence(getNewManualSequence());
            }}
          />
        </Box>
      )}
    />
  );
}

export default InsertActionModal;
